// R6 equipment one-pager: the "patient file" of ONE machine — master data, cost and breakdown
// summary (the R1/R2 ops reused with the equnr filter), open orders, past reports, measuring
// points. Composed entirely from existing endpoints.
import React, { useState } from 'react';
import { Search, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import { getKioskAdmin } from '../../api';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card, Button, StatusBadge } from '../ui';
import { Kpi } from './ReportsScreen';
import {
  searchEquipment, listOrders, equipmentNotifications, listMeasuringPoints,
  getReportCosts, getReportBreakdowns,
} from '../../api';

const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });

// Open = everything before TECO. SAP raises the order header to PCNF/CNF on the first operation
// confirmation while the order stays open, so an exact CRTD/REL test undercounted every order
// somebody had already booked time on (PM code audit 04). Kept in sync with the same list in
// components/OrdersList.js — the two predicates must not drift apart.
const OPEN_STATUS = ['CRTD', 'REL', 'PCNF', 'CNF'];

export default function EquipmentTab({ me, user, onOpenOrder }) {
  /** Print window with the machine's QR label. Kiosk URL when configured (admin-readable
   *  token); plain equipment number otherwise — the internal scan understands both. */
  const printLabel = async (eq) => {
    let payload = eq.equipmentId;
    try {
      const kiosk = await getKioskAdmin(user);
      if (kiosk && kiosk.enabled && kiosk.token) {
        payload = `${window.location.origin}/kiosk?t=${encodeURIComponent(kiosk.token)}&equnr=${encodeURIComponent(eq.equipmentId)}`;
      }
    } catch { /* no admin right -> number-only label, still scannable internally */ }
    const dataUrl = await QRCode.toDataURL(payload, { width: 360, margin: 1 });
    const w = window.open('', '_blank', 'width=420,height=560');
    if (!w) return;
    w.document.write(`<!doctype html><title>QR ${eq.equipmentId}</title>
      <body style="font-family:system-ui;text-align:center;padding:24px">
      <img src="${dataUrl}" style="width:300px;height:300px" />
      <h2 style="margin:8px 0 2px">${eq.equipmentId}</h2>
      <p style="margin:0;color:#555">${(eq.description || '').replace(/</g, '&lt;')}</p>
      <p style="margin:14px 0 0;font-size:12px;color:#888">Scan to report a problem / Scannen, um ein Problem zu melden</p>
      </body>`);
    w.document.close();
    // Print from the opener once the image rendered — avoids injecting a <script> tag.
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* window closed */ } }, 400);
  };
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const canMoney = me.profile.functionRights.canViewCosts;

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState(null);
  const [profile, setProfile] = useState(null); // { equipment, orders, notifs, points, costs, breakdowns }
  const [error, setError] = useState(null);
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  const find = async () => {
    setHits(null); setProfile(null); setError(null);
    try {
      const res = await searchEquipment(user, query, role);
      setHits((res.items || []).slice(0, 6));
    } catch (e) { setError(e.message); }
  };

  const open = async (eq) => {
    setProfile({ equipment: eq, loading: true });
    setHits(null);
    try {
      const [orders, notifs, points, costs, breakdowns] = await Promise.all([
        listOrders(user, { equipmentId: eq.equipmentId }, role),
        equipmentNotifications(user, eq.equipmentId, role),
        listMeasuringPoints(user, eq.equipmentId, role).catch(() => ({ items: [] })),
        canMoney ? getReportCosts(user, { equipmentId: eq.equipmentId }, role).catch(() => null) : Promise.resolve(null),
        getReportBreakdowns(user, { equipmentId: eq.equipmentId, days: 365 }, role).catch(() => null),
      ]);
      setProfile({ equipment: eq, orders: orders.items || [], notifs: notifs.items || [], points: points.items || [], costs, breakdowns });
    } catch (e) {
      setError(e.message);
      setProfile(null);
    }
  };

  return (
    <div className="space-y-3">
      {error && <Card><p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p></Card>}
      <Card>
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') find(); }}
            placeholder={t('pm.equip.searchPlaceholder')}
            className={`w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`} />
          <Button variant="secondary" className="!min-h-[44px] !w-auto px-4" onClick={find}><Search size={18} /></Button>
        </div>
        {hits && hits.map((h) => (
          <button key={h.equipmentId} type="button" className="w-full text-left" onClick={() => open(h)}>
            <p className={`text-sm py-1 underline ${sub}`}>{h.equipmentId} — {h.description}</p>
          </button>
        ))}
      </Card>

      {profile && profile.loading && <Card><p className={sub}>{t('pm.common.loading')}</p></Card>}
      {profile && !profile.loading && (
        <>
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {profile.equipment.equipmentId} — {profile.equipment.description}
                </h3>
                <p className={`text-sm mt-0.5 ${sub}`}>
                  {t('pm.notif.plant')} {profile.equipment.plant}
                  {profile.equipment.functionalLocation ? ` · ${profile.equipment.functionalLocation}` : ''}
                </p>
              </div>
              {/* Printable QR label: carries the kiosk URL when the kiosk is enabled (one scan =
                  operator reporting), otherwise just the equipment number (internal scan). */}
              <button type="button" onClick={() => printLabel(profile.equipment)}
                className={`flex items-center gap-1 text-xs font-medium ${sub}`}>
                <QrCode size={16} /> {t('pm.rep.qrLabel')}
              </button>
            </div>
          </Card>
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.openOrdersK')} value={profile.orders.filter((o) => OPEN_STATUS.includes(o.status)).length} />
            {profile.breakdowns && (
              <Kpi label={t('pm.rep.breakdowns12m')} value={profile.breakdowns.breakdowns}
                tone={profile.breakdowns.breakdowns > 0 ? 'warn' : 'good'} />
            )}
            {profile.costs && <Kpi label={t('pm.rep.actualCostsYear')} value={fmt(profile.costs.actualTotal)} />}
            {profile.breakdowns && profile.breakdowns.topEquipment[0] && profile.breakdowns.topEquipment[0].completeWindows > 0 && (
              <Kpi label="MTTR" value={`${fmt(profile.breakdowns.topEquipment[0].mttrHours)} h`} />
            )}
          </div>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.recentOrders')}</p>
            {profile.orders.length === 0 ? <p className={`text-sm ${sub}`}>{t('pm.orders.empty')}</p>
              : profile.orders.slice(0, 8).map((o) => (
                <button key={o.orderId} type="button" className="w-full text-left" onClick={() => onOpenOrder && onOpenOrder(o.orderId)}>
                  <div className="flex items-center justify-between py-1 text-sm">
                    <span className={`truncate mr-2 ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                      {o.orderId.replace(/^0+/, '')} — {o.description}
                    </span>
                    <StatusBadge status={o.status} />
                  </div>
                </button>
              ))}
          </Card>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.recentNotifs')}</p>
            {profile.notifs.length === 0 ? <p className={`text-sm ${sub}`}>{t('pm.notif.empty')}</p>
              : profile.notifs.slice(0, 8).map((n) => (
                <div key={n.notificationId} className="flex items-center justify-between py-1 text-sm">
                  <span className={`truncate mr-2 ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                    {n.notificationId.replace(/^0+/, '')} — {n.description}
                  </span>
                  <StatusBadge status={n.status} />
                </div>
              ))}
          </Card>
          {profile.points.length > 0 && (
            <Card>
              <p className="font-semibold text-sm mb-2">{t('pm.equip.readingsTitle')}</p>
              {profile.points.map((p) => (
                <p key={p.point} className={`text-sm py-0.5 ${sub}`}>
                  {p.point} — {p.description}{p.unit ? ` (${p.unit})` : ''}{p.isCounter ? ` · ${t('pm.equip.counter')}` : ''}
                </p>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
